// MARK://dhk8/pos/features/floor/FloorCanvas
// Read-only floor plan canvas for /floor view mode (item F — reestructura
// visual). Renders each table at its mapped position with:
//   • a clear status color code (E1 model)
//   • a notification badge with count when the table has PENDING guest
//     notifications (E2)
//   • the table code + capacity, legibly
// Tapping a table calls onSelectTable (parent opens the TableSheet).
//
// Pan/zoom + drag-to-arrange stay in <FloorEditor> (edit mode). This component
// is intentionally simple: monitoring, not editing.

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { TableDto } from '@dockethub/ui-restaurant'
import { Bell, Plus, Minus, Maximize } from 'lucide-react'

/** Canonical status → es-MX label + css modifier. DIRTY aliases CLEANING. */
const STATUS_LABEL: Record<TableDto['status'], string> = {
  AVAILABLE: 'Disponible',
  OCCUPIED: 'Ocupada',
  RESERVED: 'Reservada',
  DIRTY: 'Por limpiar',
  CLEANING: 'Por limpiar',
  UNAVAILABLE: 'Fuera de servicio',
}
function statusClass(s: TableDto['status']): string {
  return s === 'DIRTY' ? 'cleaning' : s.toLowerCase()
}

export interface FloorCanvasProps {
  tables: TableDto[]
  floorWidthPx: number
  floorHeightPx: number
  /** map table.id → count of PENDING guest notifications (E2 badge). */
  notificationCountByTable: Map<string, number>
  /** map table.id → section color (border tint). */
  sectionColorFor?: (table: TableDto) => string | undefined
  onSelectTable: (table: TableDto) => void
  /** Lane #163-bis — cuando hay filtro de sección activo, las mesas usan
   *  el color de sección como BORDE primario (más grueso) en vez de solo
   *  la barrita superior sutil. El estado se preserva como tinte de fondo. */
  emphasized?: boolean
}

export function FloorCanvas({
  tables,
  floorWidthPx,
  floorHeightPx,
  notificationCountByTable,
  sectionColorFor,
  onSelectTable,
  emphasized = false,
}: FloorCanvasProps) {
  // Lane #165 — responsive viewport + zoom controls + auto-fit:
  //   - El plane de diseño puede ser más alto que ancho (o viceversa) y en
  //     mobile el canvas se "comía" la pantalla porque el alto no se adaptaba
  //     al fit-to-width. Ahora calculamos un fit que respete AMBOS ejes y
  //     dejamos la altura del viewport explícitamente.
  //   - Botones de zoom + auto-fit a las mesas visibles (rectángulo bounding).
  const viewportRef = useRef<HTMLDivElement>(null)
  // `autoScale` = el scale calculado por fit (cambia con viewport). El user
  // puede sobreescribir con zoom in/out (zoomMultiplier sobre autoScale).
  const [autoScale, setAutoScale] = useState(1)
  const [zoomMult, setZoomMult] = useState(1)
  const [viewportH, setViewportH] = useState(400)

  // Bounding rect de las mesas visibles — para "Ajustar" dinámico.
  const bounds = useMemo(() => {
    if (tables.length === 0) {
      return { x: 0, y: 0, w: floorWidthPx, h: floorHeightPx }
    }
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
    for (const t of tables) {
      if (t.posX < minX) minX = t.posX
      if (t.posY < minY) minY = t.posY
      if (t.posX + t.widthPx > maxX) maxX = t.posX + t.widthPx
      if (t.posY + t.heightPx > maxY) maxY = t.posY + t.heightPx
    }
    // Padding alrededor del bounding (8% del lado más grande, mínimo 20px).
    const pad = Math.max(20, Math.max(maxX - minX, maxY - minY) * 0.08)
    return {
      x: Math.max(0, minX - pad),
      y: Math.max(0, minY - pad),
      w: Math.min(floorWidthPx, maxX - minX + pad * 2),
      h: Math.min(floorHeightPx, maxY - minY + pad * 2),
    }
  }, [tables, floorWidthPx, floorHeightPx])

  // Recalcula el scale para fit ambas dimensiones del viewport disponible.
  useLayoutEffect(() => {
    const el = viewportRef.current
    if (!el) return
    const measure = () => {
      const w = el.clientWidth
      // Alto disponible: respetamos el menor entre 60vh y un ratio razonable.
      const maxH = Math.min(
        typeof window !== 'undefined' ? window.innerHeight * 0.65 : 600,
        700,
      )
      if (w <= 0) return
      // Fit width-bound vs height-bound — el escala más restrictivo gana.
      const sW = w / floorWidthPx
      const sH = maxH / floorHeightPx
      const s = Math.min(sW, sH, 1.5) // permite upscale hasta 1.5× para mobile
      setAutoScale(Math.max(0.1, s))
      setViewportH(Math.min(floorHeightPx * s, maxH))
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [floorWidthPx, floorHeightPx])
  // Re-measure once on mount.
  useEffect(() => {
    const el = viewportRef.current
    if (el && el.clientWidth > 0) {
      const w = el.clientWidth
      const maxH = Math.min(typeof window !== 'undefined' ? window.innerHeight * 0.65 : 600, 700)
      const sW = w / floorWidthPx
      const sH = maxH / floorHeightPx
      const s = Math.min(sW, sH, 1.5)
      setAutoScale(Math.max(0.1, s))
      setViewportH(Math.min(floorHeightPx * s, maxH))
    }
  }, [floorWidthPx, floorHeightPx])

  const scale = autoScale * zoomMult

  function handleZoomIn()  { setZoomMult((z) => Math.min(3, +(z * 1.2).toFixed(2))) }
  function handleZoomOut() { setZoomMult((z) => Math.max(0.4, +(z / 1.2).toFixed(2))) }
  function handleFit() {
    // "Ajustar" — encuadre al bounding box de mesas visibles. Calcula el
    // zoom necesario en width y height, toma el mínimo, sin upscale > 1.5×.
    const el = viewportRef.current
    if (!el) return
    const w = el.clientWidth
    const maxH = Math.min(typeof window !== 'undefined' ? window.innerHeight * 0.65 : 600, 700)
    const targetScale = Math.min(w / bounds.w, maxH / bounds.h, 1.5)
    // Ratio respecto al autoScale (que está calculado contra el plane completo).
    setZoomMult(Math.max(0.4, targetScale / autoScale))
  }

  return (
    <div className="dhk8-fcanvas">
      <div className="dhk8-fcanvas__controls" role="toolbar" aria-label="Zoom">
        <button type="button" className="dhk8-fcanvas__ctrl" onClick={handleZoomOut} title="Alejar" aria-label="Alejar">
          <Minus size={14} />
        </button>
        <span className="dhk8-fcanvas__zoom-val" aria-live="polite">{Math.round(scale * 100)}%</span>
        <button type="button" className="dhk8-fcanvas__ctrl" onClick={handleZoomIn} title="Acercar" aria-label="Acercar">
          <Plus size={14} />
        </button>
        <button type="button" className="dhk8-fcanvas__ctrl dhk8-fcanvas__ctrl--fit" onClick={handleFit} title="Ajustar a vista" aria-label="Ajustar a vista">
          <Maximize size={14} />
        </button>
      </div>
      <div
        ref={viewportRef}
        className="dhk8-fcanvas__viewport"
        style={{ height: viewportH }}
      >
        <div
          className="dhk8-fcanvas__plane"
          style={{ width: floorWidthPx, height: floorHeightPx, transform: `scale(${scale})` }}
        >
          {tables.map((t) => {
            const cls = statusClass(t.status)
            const notifs = notificationCountByTable.get(t.id) ?? 0
            const tint = sectionColorFor?.(t)
            return (
              <button
                type="button"
                key={t.id}
                className={`dhk8-fcanvas__table dhk8-fcanvas__table--${cls}${emphasized && tint ? ' dhk8-fcanvas__table--zone-emph' : ''}`}
                style={{
                  left: t.posX,
                  top: t.posY,
                  width: t.widthPx,
                  height: t.heightPx,
                  // NintaiGO: sin border-radius en mesas rectangulares (sólo
                  // las redondas — round es la "forma" de la mesa, no un
                  // chamfered corner del sistema de diseño).
                  borderRadius: t.shape === 'ROUND' ? '50%' : '0',
                  ...(tint ? { ['--dhk8-fcanvas-tint' as string]: tint } : {}),
                }}
                onClick={() => onSelectTable(t)}
                aria-label={`Mesa ${t.code} — ${STATUS_LABEL[t.status]}${notifs > 0 ? ` — ${notifs} aviso(s) del comensal` : ''}`}
              >
                {tint && <span className="dhk8-fcanvas__tint-bar" aria-hidden="true" />}
                <span className="dhk8-fcanvas__code">{t.code}</span>
                <span className="dhk8-fcanvas__cap">{t.capacity}p</span>
                {notifs > 0 && (
                  <span className="dhk8-fcanvas__badge" aria-hidden="true">
                    <Bell size={11} />
                    <span className="dhk8-fcanvas__badge-count">{notifs}</span>
                  </span>
                )}
              </button>
            )
          })}
        </div>
      </div>
      <style>{styles}</style>
    </div>
  )
}

const styles = `
  /* NintaiGO design rules enforced below:
       - NO border-radius (excepción: ROUND tables = physical shape; not a tokenized corner).
       - NO box-shadow. Elevation/hierarchy = 1px border or bg/fg inversion. */
  .dhk8-fcanvas { width: 100%; display: flex; flex-direction: column; gap: 6px; }

  /* Lane #165 — toolbar de zoom (responsive mobile-friendly). */
  .dhk8-fcanvas__controls {
    display: flex; align-items: center; gap: 4px;
    padding: 4px;
    background: var(--pos-surface, #161C18);
    border: 1px solid var(--pos-separator-strong, rgba(255,255,255,.14));
    align-self: flex-end;
  }
  .dhk8-fcanvas__ctrl {
    min-width: 32px; min-height: 32px;
    display: inline-flex; align-items: center; justify-content: center;
    background: transparent; color: var(--pos-fg-secondary, #B6BFB8);
    border: 1px solid transparent;
    font: inherit; cursor: pointer;
    transition: background 120ms ease, color 120ms ease;
  }
  .dhk8-fcanvas__ctrl:hover { background: var(--pos-elevated, #1E2520); color: var(--pos-fg, #E8EFE9); }
  .dhk8-fcanvas__ctrl--fit { border-color: var(--pos-separator-strong, rgba(255,255,255,.14)); }
  .dhk8-fcanvas__zoom-val {
    min-width: 44px; padding: 0 4px; text-align: center;
    font-family: var(--font-mono, ui-monospace); font-size: 11px;
    color: var(--pos-fg-tertiary, #7E8C82);
  }

  .dhk8-fcanvas__viewport {
    position: relative; width: 100%;
    background:
      radial-gradient(circle at 1px 1px, color-mix(in oklch, var(--pos-fg-tertiary, #7E8C82) 22%, transparent) 1px, transparent 0);
    background-size: 28px 28px;
    background-color: var(--pos-surface, #161C18);
    border: 1px solid var(--pos-separator-strong, rgba(255,255,255,.14));
    overflow: auto; /* permite scroll cuando zoom > 100% supera el viewport */
    touch-action: pan-x pan-y; /* iOS/Android pan natural cuando hace falta */
  }
  /* The plane is laid out in the design-coordinate space and scaled to fit. */
  .dhk8-fcanvas__plane {
    position: absolute; top: 0; left: 0;
    transform-origin: top left;
  }

  .dhk8-fcanvas__table {
    position: absolute; box-sizing: border-box;
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    gap: 2px; cursor: pointer; font: inherit;
    background: var(--pos-elevated, #1E2520);
    border: 2px solid var(--pos-separator-strong, rgba(255,255,255,.16));
    color: var(--pos-fg, #E8EFE9);
    transition: transform .08s ease, border-color .12s ease;
    overflow: visible;
  }
  .dhk8-fcanvas__table:active { transform: scale(.97); }
  /* Hover uses an outline (1px border equivalent), NOT a box-shadow. */
  .dhk8-fcanvas__table:hover { outline: 2px solid var(--pos-tint, #1F8B3A); outline-offset: 1px; }

  /* E1 — state color code. Border + soft fill so it reads at a glance. */
  .dhk8-fcanvas__table--available  { border-color: var(--pos-success, #2EA043); }
  .dhk8-fcanvas__table--occupied   { border-color: var(--pos-danger, #E5484D);  background: var(--pos-danger-bg, rgba(229,72,77,.16)); }
  .dhk8-fcanvas__table--reserved   { border-color: var(--pos-warning, #E0A23C); background: var(--pos-warning-bg, rgba(224,162,60,.16)); }
  .dhk8-fcanvas__table--cleaning   { border-color: var(--pos-info, #4493F8);    background: var(--pos-info-bg, rgba(68,147,248,.16)); }
  .dhk8-fcanvas__table--unavailable { border-style: dashed; border-color: var(--pos-fg-tertiary, #7E8C82); opacity: .55; }

  .dhk8-fcanvas__code { font-family: var(--font-mono, ui-monospace); font-size: var(--pos-text-sm, 13px); font-weight: var(--pos-weight-semibold, 600); line-height: 1.1; }
  .dhk8-fcanvas__cap { font-size: 10px; color: var(--pos-fg-tertiary, #7E8C82); line-height: 1; }

  /* Section tint — thin straight bar at the top edge. */
  .dhk8-fcanvas__tint-bar {
    position: absolute; top: 3px; left: 50%; transform: translateX(-50%);
    width: 40%; height: 3px;
    background: var(--dhk8-fcanvas-tint, transparent);
  }

  /* Lane #163-bis — zone emphasis mode: cuando hay filtro de sección activo,
     pintamos el borde de la mesa con el color de la sección (más grueso) +
     barra superior más ancha + fondo más fuerte del color de zona, para que
     visualmente quede CLARO qué mesa pertenece a qué zona. El estado (color)
     queda como outline interno via box-shadow (excepción documentada al rule
     "NO box-shadow" — necesario porque ya usamos border para zona). */
  .dhk8-fcanvas__table--zone-emph {
    border-color: var(--dhk8-fcanvas-tint, currentColor) !important;
    border-width: 3px;
    background:
      color-mix(in oklch, var(--dhk8-fcanvas-tint, transparent) 14%, var(--pos-elevated, #1E2520));
  }
  .dhk8-fcanvas__table--zone-emph .dhk8-fcanvas__tint-bar {
    width: 70%;
    height: 4px;
    top: 4px;
  }

  /* E2 — notification badge: square chip (NintaiGO rule), inverted colors so
     it pops without needing a shadow. */
  .dhk8-fcanvas__badge {
    position: absolute; top: -9px; right: -9px;
    display: inline-flex; align-items: center; gap: 2px;
    min-width: 22px; height: 22px; padding: 0 5px;
    background: var(--pos-warning, #E0A23C); color: #1a1205;
    border: 2px solid var(--pos-surface, #161C18);
    font-size: 11px; font-weight: 800;
    animation: dhk8-fcanvas-pulse 1.8s ease-in-out infinite;
  }
  .dhk8-fcanvas__badge-count { line-height: 1; }
  /* Pulse via outline (no box-shadow). */
  @keyframes dhk8-fcanvas-pulse {
    0%, 100% { outline: 0 solid color-mix(in oklch, var(--pos-warning, #E0A23C) 60%, transparent); outline-offset: 0; }
    50%      { outline: 4px solid color-mix(in oklch, var(--pos-warning, #E0A23C) 0%, transparent); outline-offset: 0; }
  }
  @media (prefers-reduced-motion: reduce) {
    .dhk8-fcanvas__badge { animation: none; }
  }
`
